import { Button, Icon } from '@pypestream/design-system';
import { FC, useMemo, useState } from 'react';

import { tableStore } from '../../stores/table-store';
import { CountChips, KindTag, agentBarColor } from './diff-parts';
import type {
  FieldChange,
  NodeChange,
  NodeChangeKind,
  SolutionDiff,
} from './solution-diff';

type Filter = 'all' | NodeChangeKind;

const FILTERS: {
  id: Filter;
  label: string;
  icon?: 'plus' | 'pencil' | 'minus';
}[] = [
  { id: 'all', label: 'All' },
  { id: 'added', label: 'Added', icon: 'plus' },
  { id: 'edited', label: 'Edited', icon: 'pencil' },
  { id: 'removed', label: 'Removed', icon: 'minus' },
];

const nodeTypeLabel = (type: string) =>
  type.toLowerCase() === 'd'
    ? 'Decision Node'
    : type.toLowerCase() === 'a'
      ? 'Action Node'
      : type;

const summaryFor = (change: NodeChange) =>
  change.kind === 'added'
    ? 'Added'
    : change.kind === 'removed'
      ? 'Removed'
      : `${change.fields.length} field${change.fields.length === 1 ? '' : 's'} edited`;

/** The prototype's dark split view: older version left, newer right. */
const CodeDiff: FC<{
  field: FieldChange;
  beforeLabel: string;
  afterLabel: string;
}> = ({ field, beforeLabel, afterLabel }) => {
  const before = field.before ? field.before.split('\n') : [];
  const after = field.after ? field.after.split('\n') : [];
  const lines = Math.max(before.length, after.length, 1);
  return (
    <table className="c-deploy-code">
      {/* Fixed layout sizes columns from here; the colspan header can't. */}
      <colgroup>
        <col className="c-deploy-code__col-ln" />
        <col className="c-deploy-code__col-mk" />
        <col />
        <col className="c-deploy-code__col-ln" />
        <col className="c-deploy-code__col-mk" />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th colSpan={3}>{beforeLabel}</th>
          <th colSpan={3}>{afterLabel}</th>
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: lines }, (_, i) => (
          <tr key={i}>
            <td
              className={`c-deploy-code__ln${before[i] !== undefined ? ' is-del' : ''}`}
            >
              {before[i] !== undefined ? i + 1 : ''}
            </td>
            <td
              className={`c-deploy-code__mk${before[i] !== undefined ? ' is-del' : ''}`}
            >
              {before[i] !== undefined ? '−' : ''}
            </td>
            <td
              className={`c-deploy-code__code${before[i] !== undefined ? ' is-del' : ' is-fill'}`}
            >
              {before[i] ?? ''}
            </td>
            <td
              className={`c-deploy-code__ln${after[i] !== undefined ? ' is-add' : ''}`}
            >
              {after[i] !== undefined ? i + 1 : ''}
            </td>
            <td
              className={`c-deploy-code__mk${after[i] !== undefined ? ' is-add' : ''}`}
            >
              {after[i] !== undefined ? '+' : ''}
            </td>
            <td
              className={`c-deploy-code__code${after[i] !== undefined ? ' is-add' : ' is-fill'}`}
            >
              {after[i] ?? ''}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const NodeCard: FC<{
  change: NodeChange;
  open: boolean;
  onToggle: () => void;
  beforeLabel: string;
  afterLabel: string;
}> = ({ change, open, onToggle, beforeLabel, afterLabel }) => {
  const canOpen = change.kind !== 'removed' && !!change.rowId;
  return (
    <div className="c-deploy-node-card" data-testid="bobcat-deploy-node-card">
      <button
        type="button"
        className="c-deploy-node-card__head"
        aria-expanded={open}
        onClick={onToggle}
      >
        <KindTag kind={change.kind} />
        <span
          className={`c-deploy-node-card__name${
            change.kind === 'removed' ? ' is-removed' : ''
          }`}
        >
          {change.nodeName || 'Untitled node'}
          <span className="c-deploy-node-number">{change.nodeNumber}</span>
        </span>
        <span className="c-deploy-node-card__type">
          {nodeTypeLabel(change.nodeType)}
        </span>
        <span className="c-deploy-node-card__sum">{summaryFor(change)}</span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size="xsmall" />
      </button>
      {open && (
        <div className="c-deploy-node-card__body">
          {change.fields.map((field) => (
            <div
              key={`${field.field}:${field.path.join('.')}`}
              className="c-deploy-field"
            >
              <div className="c-deploy-field__path">
                {[field.label, ...field.path].join(' › ')}
              </div>
              <CodeDiff
                field={field}
                beforeLabel={beforeLabel}
                afterLabel={afterLabel}
              />
            </div>
          ))}
          {change.kind === 'added' && (
            <p className="c-deploy-note">New in this version.</p>
          )}
          {change.kind === 'removed' && (
            <p className="c-deploy-note">Not in this version.</p>
          )}
          {canOpen && (
            <div className="c-deploy-node-card__jump">
              <Button
                variant="secondary"
                size="small"
                test-id="bobcat-deploy-open-node"
                onClick={() => tableStore.openNodeInBuild(change.rowId!)}
              >
                <Icon name="external-link" slot="prefix" size="xsmall" />
                Open node {change.nodeNumber} in the draft
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const DiffView: FC<{
  diff: SolutionDiff;
  beforeLabel: string;
  afterLabel: string;
}> = ({ diff, beforeLabel, afterLabel }) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [collapsedAgents, setCollapsedAgents] = useState<Set<string>>(
    new Set()
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (change: NodeChange) =>
      (filter === 'all' || change.kind === filter) &&
      (!q ||
        change.nodeName.toLowerCase().includes(q) ||
        change.nodeNumber.includes(q) ||
        change.fields.some(
          (f) =>
            f.label.toLowerCase().includes(q) ||
            f.path.join('.').toLowerCase().includes(q)
        ));
  }, [filter, query]);

  const shown = diff.changes.filter(matches);
  const keyOf = (c: NodeChange) => `${c.kind}:${c.nodeNumber}:${c.nodeName}`;
  const allOpen = shown.length > 0 && shown.every((c) => open.has(keyOf(c)));

  const toggle = <T,>(set: Set<T>, value: T) => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };

  return (
    <div className="c-deploy-diff" data-testid="bobcat-deploy-diff">
      <div className="c-deploy-diff__bar">
        <div className="c-deploy-diff__chips">
          {FILTERS.map((f) => {
            const n = f.id === 'all' ? diff.changes.length : diff.counts[f.id];
            return (
              <button
                key={f.id}
                type="button"
                className={`c-deploy-chip${filter === f.id ? ' is-active' : ''}`}
                aria-pressed={filter === f.id}
                onClick={() => setFilter(f.id)}
              >
                {f.icon && <Icon name={f.icon} size="xsmall" />}
                {f.label}
                <span className="c-deploy-chip__n">{n}</span>
              </button>
            );
          })}
        </div>
        <label className="c-deploy-diff__find">
          <Icon name="search" size="xsmall" />
          <input
            type="search"
            placeholder="Find a node or field…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
      </div>
      <div className="c-deploy-diff__meta">
        <span>
          {shown.length} of {diff.changes.length} nodes
        </span>
        {shown.length > 0 && (
          <button
            type="button"
            className="c-deploy-link"
            onClick={() =>
              setOpen(allOpen ? new Set() : new Set(shown.map(keyOf)))
            }
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        )}
      </div>

      {diff.agents.map((agent) => {
        const list = agent.changes.filter(matches);
        if (!list.length) return null;
        const collapsed = collapsedAgents.has(agent.name);
        const color = agentBarColor(agent);
        return (
          <section key={agent.name} className="c-deploy-diff__agent">
            <button
              type="button"
              className={`c-deploy-agent-bar${agent.isUngrouped ? ' is-ungrouped' : ''}`}
              style={color ? { background: color } : undefined}
              aria-expanded={!collapsed}
              onClick={() =>
                setCollapsedAgents(toggle(collapsedAgents, agent.name))
              }
            >
              <span className="c-deploy-agent-bar__name">{agent.name}</span>
              <CountChips counts={agent} />
              <span className="c-deploy-agent-bar__n">
                {list.length} node{list.length === 1 ? '' : 's'}
              </span>
              <Icon
                name={collapsed ? 'chevron-right' : 'chevron-down'}
                size="xsmall"
              />
            </button>
            {!collapsed &&
              list.map((change) => (
                <NodeCard
                  key={keyOf(change)}
                  change={change}
                  open={open.has(keyOf(change))}
                  onToggle={() => setOpen(toggle(open, keyOf(change)))}
                  beforeLabel={beforeLabel}
                  afterLabel={afterLabel}
                />
              ))}
          </section>
        );
      })}
      {!shown.length && <p className="c-deploy-note">No changes match.</p>}
    </div>
  );
};
